import React, {createRef} from 'react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import './ImagePreview.scss';
import {back, pancel} from '../../icons';
import {ITranslate} from '../../../../../services/LocalizationService';
import {observer} from 'mobx-react';
import {WifiHeatmapPreviewComponent} from './WifiHeatmapPreviewComponent';
import {LayeredImageController} from '../../../../../shared/image-layers/LayeredImageController';

interface ImagePreviewProps {
    imageUrl: string;
    imageLabel?: string;
    onBack: () => void;
    onEdit: (imageUrl: string, imageLabel?: string) => void;
    translate: ITranslate;
    layeredImageController: LayeredImageController;
    mergeImageWithScaleBar: (imageElement: HTMLImageElement) => Promise<string | null>;
}

@observer
export class ImagePreview extends TechseeBaseComponent<ImagePreviewProps> {
    private imgRef = createRef<HTMLImageElement>();

    private handleEdit = async () => {
        const {imageUrl, imageLabel, onEdit, layeredImageController, mergeImageWithScaleBar} = this.props;

        const hasLayers = layeredImageController.getVisibleLayers().length > 0;

        if (!hasLayers) {
            onEdit(imageUrl, imageLabel);
            return;
        }

        // Reuse already-loaded image from the preview (no network request needed)
        const img = this.imgRef.current;
        if (!img || !img.complete) {
            onEdit(imageUrl, imageLabel);
            return;
        }

        // Merge image with layers using layeredImageController
        try {
            const mergedUrl = await mergeImageWithScaleBar(img);
            onEdit(mergedUrl || imageUrl, imageLabel);
        } catch {
            onEdit(imageUrl, imageLabel);
        }
    };

    renderInternal(): JSX.Element {
        const {imageUrl, imageLabel, onBack, translate, layeredImageController} = this.props;

        return (
            <div className='image-preview-container'>
                <div className='image-preview-header'>
                    <button className='image-preview-button back-button' onClick={onBack}>
                        <span>{back}</span>
                        <span>{translate('DEVICE_DETECTED.VIEW.DEVICE_BACK')}</span>
                    </button>
                    <button className='edit-button-box edit-button' onClick={this.handleEdit}>
                        <span className='pencil-icon'>{pancel}</span>
                        <span>{translate('CONNECTIVITY_GURU.VISUAL_SCAN.EDIT')}</span>
                    </button>
                </div>
                <div className='image-preview-content'>
                    <WifiHeatmapPreviewComponent
                        layeredImageController={layeredImageController}
                        className='image-preview-wrapper'>
                        <img
                            ref={this.imgRef}
                            src={imageUrl}
                            alt={imageLabel || 'Heatmap Preview'}
                            className='image-preview-img'
                            crossOrigin='anonymous'
                        />
                    </WifiHeatmapPreviewComponent>
                </div>
            </div>
        );
    }
}
